<template>
<div>
  <transition name="medium-fade">
    <div
      v-if="activeTags.length"
      class="reference-slider"
      :class="[
        {'reference-slider--vert': isVertical},
        {'reference-slider--sub-bottom-bar': isSubBottomBarVisible},
      ]"
    >
      <div class="tag-slider-controls">
        <div class="tag-slider-prev">
          <fa :icon="['fal', 'chevron-left']"/>
        </div>
        <div class="tag-slider-next">
          <fa :icon="['fal', 'chevron-right']"/>
        </div>
      </div> 
      <div class="reference-container touch">
        <tiny-slider 
          class="touch"
          v-bind="isVertical ? verticalTinySliderOptions : tinySliderOptions"
          :startIndex="activeLanguage.rtl ? activeTags.length - 1 : 0"
          ref="tinySlider"
          @dragMove="handleSliderDrag"
          @dragEnd="handleSliderDragEnd"
        >
          <div v-for="tag in activeTags" :key="tag._id">
            <div class="slide" :class="{ 'slide--reverse' : activeLanguage.rtl }">
              <div class="slide__image-wrapper" :class="tag.image === '' ? 'no-image' : null">
                <div v-show="tag.isVisible" class="slide__image-reference">{{ tag.referenceIndex }}</div>
                <img 
                  class="slide__image" 
                  :class="tag.image === '' ? 'no-image' : null" 
                  :src="getAsset(tag)" 
                  alt=""
                >
                <div
                  v-if="tag.asset && tag.asset.type === 'video'"
                  class="slide__video-circle-wrapper"
                  :class="{ 'slide__video-circle-wrapper--active': tag.asset.path === video }"
                >
                  <fa :icon="['fad', 'play-circle']" @click="setVideo(tag.asset.path)" />
                </div>
              </div>
              <div class="slide__content" :class="{ 'dir-rtl' : activeLanguage.rtl }">
                <div class="slide__heading" v-html="tag.label[activeLanguage.code]"></div>
                <div class="slide__description" v-html="tag.description[activeLanguage.code]"></div>
              </div>
            </div>
          </div> 
        </tiny-slider>
      </div>
    </div>
  </transition>
  <transition name="fast-fade">
    <div
      v-if="video"
      class="reference-video"
      :class="[
        {'reference-video--vert': isVertical},
        {'reference-video--sub-bottom-bar': isSubBottomBarVisible},
      ]"
    >
      <div class="reference-video__container">
        <video :src="$store.state.assetsPath + video" autoplay loop></video>
      </div>
    </div>
  </transition>
</div>
</template>

<script>
import TinySlider from 'vue-tiny-slider'
import { bus } from '../main'

export default {
  components: { TinySlider },

  props: ['tags'],

  data () {
    return {
      tinySliderOptions: {
        axis: 'horizontal',
        mouseDrag: true,
        controlsContainer: '.tag-slider-controls',
        loop: false,
        items: 3,
        gutter: 0,
        nav: false,
        speed: 600,
        swipeAngle: 45
      },

      verticalTinySliderOptions: {
        axis: 'vertical',
        mouseDrag: true,
        controlsContainer: '.tag-slider-controls',
        loop: false,
        items: 2,
        gutter: 0,
        nav: false,
        speed: 600,
        swipeAngle: 45
      },

      activeTags: [],
      dragTimeout: null,
      isSliderDragging: false,
      video: null
    }
  },

  computed: {
    activeLanguage () {
      return this.$store.getters.language
    },

    isSubBottomBarVisible () {
      return this.$store.getters.activeSubBottomBar.length > 0
    },

    isVertical () {
      return this.$store.getters.activePage ? this.$store.getters.activePage.verticalSlider : true
    }
  },

  watch: {
    $route () {
      this.video = null
    },

    tags (newTags, oldTags) {
      if (!_.isEqual(newTags, oldTags)) {
        if (oldTags.length) {
          this.activeTags = []
        }

        if (newTags.length) {
          this.$store.commit('addAnimationToQueue', 'ReferenceSlider')
        }
      }
    }
  },

  mounted () {
    bus.$on('animateReferenceSlider', () => {
      setTimeout(() => {
        this.activeTags = this.tags
        bus.$emit('finishedAnimation')
      }, 500)
    })
  },

  methods: {
    getAsset (tag) {
      if (tag.asset) {
        return this.$store.state.assetsPath + (tag.asset.customThumbPath ? tag.asset.customThumbPath : tag.asset.thumbPath)
      } else {
        return ''
      }
    },

    handleSliderDrag () {
      this.isSliderDragging = true
      if (this.dragTimeout === null) {
        this.dragTimeout = setTimeout(() => {
          this.dragTimeout = null
          this.isSliderDragging = false
        }, 1000) 
      }
    },

    handleSliderDragEnd () {
      this.dragTimeout = null
      this.isSliderDragging = false
    },

    setVideo (video) {
      if (!this.isSliderDragging) {
        if (this.video === video) {
          this.video = null
        } else {
          this.video = video
        }
      }

    }
  }
}
</script>

<style lang="scss">
@import "node_modules/tiny-slider/src/tiny-slider";
@import "@/styles/_variables.scss";

.reference-video--sub-bottom-bar.reference-video--vert {
  bottom: 15rem !important;
}

.reference-slider {
  background: rgba(#000, .5);
  bottom: 10rem;
  color: #fff;
  font-family: $touchscreen-font;
  height: 23em;
  padding: 2.35em 8em 2.4em 9.8em;
  position: absolute;
  user-select: none;
  width: 160rem;
  z-index: 50;

  * {
    pointer-events: auto !important;
  }

  .reference-container {
    display: flex;
    flex-direction: column;
    height: 100%;
    width: 100%;
  }

  .tag-slider-controls {
    position: absolute;
    top: 0;
    left: 0;
    height: 100%;
    width: 100%;
    font-size: 4em;
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 0 .75em 0 1.25em;

    div {
      transition: opacity 0.3s;

      &[aria-disabled=true] {
        opacity: 0.2;
      }
    }
  }

  .slide {
    display: flex;
    height: 100%;
    padding: 0 2em;

    &--reverse {
      flex-direction: row-reverse;

      .slide__content {
        padding: 0 1em 0 0;
      }
    }

    &__image-wrapper, &__content {
      height: 18.5em;
      position: relative;
      width: 20em;

      &.no-image {
        background-image: linear-gradient(45deg, #eee 25%, transparent 25%, transparent 75%, #eee 75%, #eee 100%), linear-gradient(45deg, #eee 25%, white 25%, white 75%, #eee 75%, #eee 100%);
        background-position: 0px 0px, 10px 10px;
        background-size: 20px 20px;
      }
    }

    &__image-reference {
      background-color: #000;
      border-radius: 1.5em;
      display: flex;
      justify-content: center;
      align-items: center;
      margin: 0.5em;
      font-size: 1.5em;
      position: absolute;
      top: 0;
      left: 0;
      height: calc(3em * 10 / 15);
      width: calc(3em * 10 / 15);
    }

    &__content {
      padding-left: 1em;
    }

    &__image {
      height: 18.5em;
      object-fit: cover;
      width: 20em;

      &.no-image {
        opacity: 0 !important;
      }
    }

    &__heading {
      font-size: 1.5em;
      font-weight: 400;
      margin-bottom: 0.5em;
      text-transform: uppercase;
      letter-spacing: 0.05em;
    }

    &__description {
      font-size: 1.5em;
      font-weight: 200;
      line-height: 1.2;
      letter-spacing: 0.05em;
    }

    &__video-circle-wrapper {
      position: absolute;
      top: 0;
      left: 0;
      height: 100%;
      width: 100%;
      display: flex;
      align-items: center;
      justify-content: center;

      svg {
        height: 10rem;
        opacity: .9;
        transition: transform 0.3s;
        width: 10rem;
      }

      &--active {
        svg {
          opacity: 1;
          transform: scale(1.1) rotate(-90deg);
        }
      }
    }
  }

  &--sub-bottom-bar {
    bottom: 15rem;
  }

  &--vert {
    right: 32.25rem;
    height: auto;
    padding: 14em 4em 8em;
    top: 0;
    width: 28em;

    .tag-slider-controls {
      bottom: 0;
      height: 2em;
      top: unset;
      padding: 0;
    }

    .tag-slider-prev, .tag-slider-next {
      padding: .5em 1.1em;
      transform: rotate(90deg);
    }

    .slide {
      display: block;
      padding: 0;
      height: 50%;

      &__image-wrapper, &__content {
        position: relative;
        width: 100%;
      }

      &__content {
        padding-bottom: 2em;
        padding-left: 0em;
      }

      &__image {
        height: 18.5em;
        object-fit: cover;
        width: 20em;
        padding-bottom: 1em;
      }

      &__heading {
        font-size: 1.5em;
        font-weight: 400;
        margin-bottom: 0.5em;
        text-transform: uppercase;
        letter-spacing: 0.05em;
      }

      &__description {
        font-size: 1.5em;
        font-weight: 200;
        line-height: 1.2;
        letter-spacing: 0.05em;
      }
    }
  }
}

.reference-video {
  background: rgba(0, 0, 0, 0.85);
  bottom: 33rem;
  left: 0;
  position: absolute;
  right: 32.25rem;
  top: 0;
  z-index: 100;

  &__container {
    bottom: 5rem;
    left: 5rem;
    position: absolute;
    right: 5rem;
    top: 5rem;
  }

  &--vert {
    bottom: 10rem;
    right: 60.25rem;

    .reference-video--sub-bottom-bar {
      bottom: 33rem;
    }
  }

  &--sub-bottom-bar {
    bottom: 38rem;
  }

  video {
    height: 100%;
    object-fit: contain;
    width: 100%;
  }
}
</style>